import { Button, Flex } from '@chakra-ui/react';
import React from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';

import { LoanMetadataEditForm } from './LoanMetadataEditForm';
import { LoansActivityTable } from './LoansActivityTable';
import { LoanEditRouteParamsStruct } from './types';
import { useEditLoanContext } from './useEditLoanContext';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';

const RouteBase = () => {
  const params = useParams();
  const { loanId } = LoanEditRouteParamsStruct.parse(params);

  const context = useEditLoanContext();

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      gap="2rem"
      padding={dashboardRoutePadding}
    >
      <Flex w="100%" justify="space-between" alignItems="center">
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Funding', path: '/' },
            { label: 'Loans', path: '/loans' },
            context.status === 'ready'
              ? {
                  label: context.state.originalResolvedLoan.name,
                  path: `/loans/${loanId}`,
                }
              : {
                  type: 'loading',
                },
          ]}
        />
        <Flex gap="1rem">
          <Button
            as={Link}
            to={`/loans/${loanId}/edit/review`}
            variant="primary"
            colorScheme="v2-aurora"
          >
            Review Changes
          </Button>
        </Flex>
      </Flex>

      <Flex width="100%" justify="space-between" alignItems="center">
        <Flex justify="space-between" alignItems="center" w="100%">
          {(() => {
            if (context.status === 'ready') {
              return (
                <Typography variant="page-title" trim>
                  Edit {context.state.originalResolvedLoan.name}
                </Typography>
              );
            }
            return (
              <Typography variant="page-title" trim>
                Edit Loan
              </Typography>
            );
          })()}
          <Flex
            borderRadius="2rem"
            borderColor="gray.200"
            borderWidth="1px"
            padding="1rem"
            alignItems="center"
          >
            <Typography fontWeight="bold">Loan</Typography>
            {(() => {
              if (context.status === 'ready') {
                return (
                  <Typography variant="base-muted">
                    {`...${loanId.substring(loanId.length - 12)}${
                      context.state.originalResolvedLoan.externalLoanId
                        ? ` (${context.state.originalResolvedLoan.externalLoanId})`
                        : ''
                    }`}
                  </Typography>
                );
              }
              return (
                <Typography>
                  ...{loanId.substring(loanId.length - 5)}
                </Typography>
              );
            })()}
          </Flex>
        </Flex>
      </Flex>

      <Typography variant="base-muted">
        Select &apos;Review Changes&apos; to submit any unsubmitted edits will
        not be saved.
      </Typography>

      <LoanMetadataEditForm />

      <Flex flexDirection="column" gap="1rem" w="100%">
        <Typography variant="section-title">Past Draws</Typography>
        <Typography variant="base-muted">
          Select a row to edit the draw request amount, including lender shares.
          The selected row will open on a separate page for edits, with the
          option to save and return.
        </Typography>

        <LoansActivityTable />
      </Flex>
    </Flex>
  );
};

export const Route = () => {
  const params = useParams();
  const { loanId } = LoanEditRouteParamsStruct.parse(params);

  return (
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to={`/loans/${loanId}`} />;
      }}
    >
      <RouteBase />
    </RouteErrorBoundary>
  );
};
